/**
 * @file ./views/layouts/danfe/producao/grade.js
 *
 * Grade de coordenadas absolutas do DANFE, em pontos tipográficos.
 *
 * A malha de retângulos foi extraída de um gerador de DANFE escrito sobre
 * `pdfkit`, onde o documento é desenhado exclusivamente com
 * `doc.rect(x, y, largura, altura)` e `doc.text(texto, x, y, opcoes)` —
 * nenhum layout de fluxo. As coordenadas transferem sem conversão para o
 * `@react-pdf/renderer` porque:
 *
 *   1. no pdfkit, `rect` é medido a partir da **origem da página**, não da
 *      margem — o mesmo que um `position: 'absolute'` numa `<Page>` com
 *      `padding: 0`;
 *   2. `Times-Roman` (base-14 do PDF) e o `Tinos` registrado por
 *      `fontesDanfe.js` compartilham as métricas do Times New Roman, então
 *      as larguras de texto que estas coordenadas pressupõem continuam
 *      válidas.
 *
 * ## Duas divergências deliberadas em relação ao gerador de origem
 *
 * **Tipografia.** O original imprime rótulos em 5 pt e conteúdo em 8 pt,
 * abaixo dos mínimos do MOC 7.0 – Anexo II (6 pt e 10 pt, itens 3.7.3 e
 * 3.7.9), entre outros oito pontos fora da norma. Aqui os corpos seguem os
 * mínimos e apenas as **linhas de base dentro das caixas** foram
 * reposicionadas — a malha de retângulos é idêntica à do original. Ver
 * {@link TIPOGRAFIA}.
 *
 * **Colunas de ST.** O item 3.1.7 só admite suprimir colunas que "não se
 * apliquem às atividades" do emitente. Como a operação envolve substituição
 * tributária, `B. CÁLC. ICMS ST` e `VALOR ICMS ST` foram acrescentadas na
 * posição que a tabela 3.8.1 determina, e as 15 colunas foram
 * reproporcionadas dentro dos mesmos 566,93 pt.
 *
 * ## Convenção de nomes
 *
 * `x`/`y` são absolutos na página. `dy` é deslocamento a partir do topo do
 * quadro de identificação, que muda de folha para folha (ver
 * {@link topoCabecalho}). `deslocamentoFatura` desloca verticalmente tudo o
 * que vem depois do quadro de fatura.
 */

/** Folha A-4 em pontos, como o pdfkit a define para `size: 'a4'`. */
export const PAGINA = { largura: 595.28, altura: 841.89 };

/**
 * Margens do documento. O gerador original usava margens laterais
 * assimétricas — 5,67 pt (0,2 cm, o mínimo do MOC 3.6.2) à esquerda e
 * 22,68 pt (0,8 cm, o máximo) à direita — o que deixava o corpo do DANFE
 * visivelmente deslocado para a direita na folha.
 *
 * As laterais aqui são iguais, em **14,175 pt (0,5 cm)** cada — o ponto
 * médio exato entre os dois extremos originais (`(5,67 + 22,68) / 2`) — o
 * que centraliza o documento na página e ainda cai bem no meio da faixa
 * permitida pelo MOC, com folga para os dois lados.
 */
export const MARGENS = {
  superior: 22,
  direita: 14.175,
  inferior: 12,
  esquerda: 14.175,
};

const L = MARGENS.esquerda;
const T = MARGENS.superior;

/** 595,28 − 14,175 − 14,175. Largura em que quase todas as bandas fecham. */
export const LARGURA_UTIL = 566.93;

/** Fio fino de todos os quadros. */
/**
 * Espessura e cor do fio de todos os quadros, mais um raio de canto sutil
 * (`raio`). O MOC não exige cantos vivos; 1,5 pt amacia visualmente sem
 * abrir vão perceptível nas divisas entre células vizinhas, mesmo nas
 * menores do documento (UF a 22 pt, CST a 17 pt de largura).
 */
export const FIO = { espessura: 0.75, cor: '#1c1c1c', raio: 1.5 };

/** Fundo das células de cabeçalho e dos totalizadores em destaque. */
export const FUNDO_DESTAQUE = '#DDDDDD';

export const CORES = {
  tinta: '#000000',
  papel: '#FFFFFF',
  alerta: '#FF0000',
  marcaDagua: '#808080',
};

/**
 * Corpos de fonte, em pontos, com o item do MOC 7.0 – Anexo II que fixa
 * cada mínimo. Reduzir qualquer um destes valores coloca o documento fora
 * da norma; aumentar é permitido, mas exige reconferir se o texto ainda
 * cabe na caixa.
 *
 * O manual admite alternativamente medidas em CPP (caracteres por
 * polegada) — são a especificação equivalente para impressoras de impacto,
 * e não se aplicam a uma geração para laser/jato como esta.
 */
export const TIPOGRAFIA = {
  /** 3.7.1 — descritivo dos blocos: mín. 5 pt, negrito, caixa alta. */
  tituloBloco: 6,
  /** 3.7.3 — descritivo dos demais campos: mín. 6 pt, caixa alta. */
  rotuloCampo: 6,
  /** 3.7.9 — conteúdo dos demais campos: mín. 10 pt. */
  conteudoCampo: 10,
  /** 3.7.2 — descritivo dos campos do quadro de produtos: mín. 5 pt. */
  rotuloProduto: 5.7,
  /** 3.7.7 — conteúdo do quadro de produtos: mín. 6 pt. */
  conteudoProduto: 6,
  /** 3.7.4 — a descrição "DANFE": mín. 12 pt, negrito. */
  danfe: 12,
  /** 3.7.4 — "DOCUMENTO AUXILIAR..." e "ENTRADA"/"SAÍDA": mín. 8 pt. */
  subtituloDanfe: 8,
  /** 3.7.4 — identificador do tipo de operação: mín. 10 pt, negrito. */
  tipoOperacao: 10,
  /** 3.7.4 — número, série, ordem e total de folhas: mín. 10 pt, negrito. */
  numeroSerieFolha: 10,
  /** 3.7.6 — razão social / nome fantasia: mín. 12 pt, negrito. */
  razaoSocialEmitente: 12,
  /** 3.7.6 — endereço, município, CEP, fone/fax do emitente: mín. 8 pt, negrito. */
  dadosEmitente: 8,
  /** 3.7.5 — chave de acesso: negrito (o manual não fixa corpo mínimo). */
  chaveAcesso: 7,
  /** 3.7.8 — informações complementares: mín. 6 pt. */
  informacoesComplementares: 6,
  /** Texto instrucional de consulta; não é conteúdo de campo. */
  consultaAutenticidade: 6.5,
  /**
   * Carimbo "Impresso: dd/mm/aaaa hh:mm:ss". Não é campo do DANFE — o MOC
   * não fixa corpo mínimo para ele —, por isso usa o menor tamanho ainda
   * legível no papel, abaixo de todos os mínimos normativos acima.
   */
  carimboImpressao: 4,
};

/**
 * Linhas de base dentro de uma banda de 23 pt.
 *
 * O original usava rótulo em `y+5` e valor em `y+15`, folgado para 5 e 8 pt.
 * Com 6 e 10 pt o valor terminaria em `y+26,5` e vazaria a banda, então
 * ambos sobem: rótulo ocupa `y+2` a `y+8,9` e valor `y+10,5` a `y+22`,
 * deixando 1 pt de folga até o fio inferior.
 */
export const RECUO = { rotulo: 2, valor: 10.5 };

/** Altura padrão de uma banda de campos. */
export const ALTURA_BANDA = 23;

/**
 * Largura que o pdfkit assume quando `doc.text` é chamado sem `width`: da
 * posição do texto até a margem direita. Importa nos campos com
 * `align: 'center'`/`'right'`, onde a largura implícita define onde o texto
 * de fato cai.
 *
 * @param {number} x - Coordenada horizontal do texto.
 * @returns {number} Largura implícita, em pontos.
 */
export function larguraRestante(x) {
  return PAGINA.largura - MARGENS.direita - x;
}

/**
 * Topo do quadro de identificação. Na primeira folha ele desce 52 pt para
 * abrir espaço ao canhoto; nas folhas de continuação encosta na margem.
 *
 * @param {boolean} primeiraFolha
 * @returns {number} Coordenada vertical, em pontos.
 */
export function topoCabecalho(primeiraFolha) {
  return T + (primeiraFolha ? 52 : 0);
}

/**
 * Recuo horizontal recorrente de 4,33 pt entre o início de uma caixa e o
 * texto dentro dela — herdado do gerador original, que usava coordenadas de
 * texto absolutas e fixas (`x: 10`, `x: 480`, `x: 418`...) em vez de
 * relativas à margem. Isso só não dava problema porque a margem nunca
 * mudava; aparece assim no canhoto, no quadro do emitente e no rodapé.
 * Expressas como `caixa.x + RECUO_TEXTO_HERDADO`, essas coordenadas passam a
 * acompanhar `L` (e, por tabela, `MARGENS.esquerda`) sempre que ele mudar —
 * o que se provou necessário na prática ao centralizar a página (ver
 * `MARGENS`).
 */
const RECUO_TEXTO_HERDADO = 4.33;

/**
 * Estilo de destaque a pedido do usuário para campos secundários (a maior
 * parte do Destinatário/Remetente e da Identificação do Emitente, exceto a
 * razão social, o endereço e o município): centralizado e negrito, no
 * corpo padrão de `TIPOGRAFIA.conteudoCampo` (10 pt).
 *
 * O pedido original reduzia também para 8 pt, abaixo do mínimo do MOC 7.0 –
 * Anexo II, item 3.7.9 ("Conteúdo dos Demais Campos") — nem o Destinatário
 * nem Natureza da Operação/Protocolo/Inscrições/CNPJ do emitente têm uma
 * exceção mais permissiva na norma (diferente do endereço do emitente, que
 * o item 3.7.6 já autoriza em 8 pt). O usuário optou por manter o corpo em
 * conformidade e ficar só com negrito + centralização.
 */
const VALOR_NEGRITO_CENTRALIZADO = {
  corpo: TIPOGRAFIA.conteudoCampo,
  alinhamento: 'center',
  negrito: true,
};

/**
 * Canhoto de recebimento e fio de recorte — só na primeira folha. O MOC
 * 3.3.1 permite suprimi-lo, devolvendo o espaço ao quadro de produtos.
 */
export const CANHOTO = {
  declaracao: { x: L, y: T, largura: 470, altura: 21 },
  recebemos: {
    x: L + RECUO_TEXTO_HERDADO,
    y: 25,
    corpo: TIPOGRAFIA.rotuloCampo,
    espacamento: 0.5,
    largura: 462,
  },
  emissao: {
    x: L + RECUO_TEXTO_HERDADO,
    y: 33,
    corpo: TIPOGRAFIA.rotuloCampo,
    espacamento: 0.5,
    largura: 462,
  },

  caixaNfe: { x: L + 470, y: T, largura: 96.93, altura: 42 },
  tituloNfe: {
    x: L + 470 + RECUO_TEXTO_HERDADO,
    y: 25,
    corpo: TIPOGRAFIA.numeroSerieFolha,
    espacamento: 1.5,
    alinhamento: 'center',
    negrito: true,
  },
  numeroNfe: {
    x: L + 470 + RECUO_TEXTO_HERDADO,
    y: 38,
    corpo: TIPOGRAFIA.numeroSerieFolha,
    espacamento: 1,
    alinhamento: 'center',
    negrito: true,
  },
  serieNfe: {
    x: L + 470 + RECUO_TEXTO_HERDADO,
    y: 50,
    corpo: TIPOGRAFIA.numeroSerieFolha,
    espacamento: 1,
    alinhamento: 'center',
    negrito: true,
  },

  // Largura de 75pt (a do gerador original) quebra "DATA DE RECEBIMENTO" em
  // duas linhas nos 6pt mínimos do MOC 3.7.3 — confirmado empiricamente
  // (70 e 75pt quebram com hífen; 80pt já é suficiente). 95pt dá folga
  // confortável para uma linha só; a diferença é devolvida à caixa de
  // assinatura, que sobra bastante espaço.
  caixaData: { x: L, y: T + 21, largura: 95, altura: 21 },
  rotuloData: {
    x: L + RECUO_TEXTO_HERDADO,
    y: 46,
    corpo: TIPOGRAFIA.rotuloCampo,
    espacamento: 0.5,
    largura: 90,
  },

  caixaAssinatura: { x: L + 95, y: T + 21, largura: 375, altura: 21 },
  rotuloAssinatura: {
    x: L + 95 + RECUO_TEXTO_HERDADO,
    y: 46,
    corpo: TIPOGRAFIA.rotuloCampo,
    espacamento: 0.5,
    largura: 365,
  },

  recorte: { x: L, y: 69, largura: LARGURA_UTIL },
};

/**
 * Quadro de identificação: emitente, bloco DANFE, código de barras/chave,
 * natureza da operação, protocolo e inscrições. É o bloco que o MOC 3.5
 * obriga a repetir em toda folha — `dy` é relativo a
 * {@link topoCabecalho}.
 */
export const CABECALHO = {
  emitente: {
    caixa: { x: L, dy: 0, largura: 197.5, altura: 98 },
    rotulo: {
      x: L + RECUO_TEXTO_HERDADO,
      dy: 2,
      corpo: TIPOGRAFIA.rotuloCampo,
      espacamento: 0.5,
      largura: 190,
    },
    /**
     * `logo_com_dados.png` foi trocado por `LOGO_PRETA.png` — só a marca,
     * sem texto embutido — porque um PNG não dá para provar em código que
     * atinge os corpos mínimos do MOC 3.7.6. Razão social e endereço
     * voltam a ser texto vetorial abaixo dela, nos corpos mínimos (12 pt
     * para a razão social, 8 pt negrito para o endereço) que `TIPOGRAFIA`
     * já garante — nenhum dos dois pode encolher além disso sem sair do
     * MOC, então o espaço extra pedido pelo usuário (sem o CNPJ, que
     * saiu deste quadro) vai inteiro para a logo.
     *
     * O PNG tem proporção 1426×515 (≈2,769:1); a altura é o que sobra
     * depois de reservar espaço para as duas linhas de texto (dy=60 a
     * dy=95), e a largura é derivada dela para não distorcer a logo. `x`
     * centraliza o resultado dentro dos 197,5 pt da caixa.
     */
    imagem: {
      x: L + 32.3,
      dy: 10,
      largura: 132.91,
      altura: 48,
    },
    // Largura e `x` reaproveitam o mesmo recuo de `rotulo` (197,5 − 2 ×
    // 4,33), centralizando as duas linhas dentro da caixa.
    nome: {
      x: L + RECUO_TEXTO_HERDADO,
      dy: 60,
      corpo: TIPOGRAFIA.razaoSocialEmitente,
      espacamento: 0.5,
      largura: 188.84,
      altura: 14,
      alinhamento: 'center',
      negrito: true,
    },
    // Único campo dos dois com `altura` para duas linhas: é o de maior
    // risco de estourar uma linha só (logradouro + número + bairro +
    // município/UF + CEP), então quebra em vez de truncar.
    endereco: {
      x: L + RECUO_TEXTO_HERDADO,
      dy: 76,
      corpo: TIPOGRAFIA.dadosEmitente,
      espacamento: 0.5,
      largura: 188.84,
      altura: 19,
      alinhamento: 'center',
      negrito: true,
    },
  },

  danfe: {
    caixa: { x: L + 197.5, dy: 0, largura: 112.7, altura: 98 },
    titulo: {
      x: L + 197.5,
      dy: 6,
      corpo: TIPOGRAFIA.danfe,
      espacamento: 1.5,
      largura: 112.7,
      alinhamento: 'center',
      negrito: true,
    },
    /** A 8 pt o subtítulo precisa da largura cheia da coluna para caber em duas linhas. */
    subtitulo: {
      x: L + 197.5,
      dy: 21,
      corpo: TIPOGRAFIA.subtituloDanfe,
      espacamento: 0,
      largura: 112.7,
      alinhamento: 'center',
    },
    // 13,83 pt = recuo herdado do original dentro da caixa DANFE
    // (que começa em `L + 197.5`), na mesma lógica de `RECUO_TEXTO_HERDADO`.
    entrada: {
      x: L + 197.5 + 13.83,
      dy: 41,
      corpo: TIPOGRAFIA.subtituloDanfe,
      espacamento: 0.5,
    },
    saida: {
      x: L + 197.5 + 13.83,
      dy: 51,
      corpo: TIPOGRAFIA.subtituloDanfe,
      espacamento: 0.5,
    },
    caixaTipo: { x: L + 275, dy: 42, largura: 18, altura: 18 },
    tipo: {
      x: L + 275,
      dy: 45.5,
      corpo: TIPOGRAFIA.tipoOperacao,
      largura: 18,
      alinhamento: 'center',
      negrito: true,
    },
    numero: {
      x: L + 197.5,
      dy: 62,
      corpo: TIPOGRAFIA.numeroSerieFolha,
      espacamento: 0.5,
      largura: 112.7,
      alinhamento: 'center',
      negrito: true,
    },
    serie: {
      x: L + 197.5,
      dy: 74,
      corpo: TIPOGRAFIA.numeroSerieFolha,
      espacamento: 0.5,
      largura: 112.7,
      alinhamento: 'center',
      negrito: true,
    },
    folha: {
      x: L + 197.5,
      dy: 86,
      corpo: TIPOGRAFIA.numeroSerieFolha,
      espacamento: 0.5,
      largura: 112.7,
      alinhamento: 'center',
      negrito: false,
    },
  },

  chave: {
    caixaBarras: { x: L + 310.2, dy: 0, largura: 256.73, altura: 35 },
    barras: { x: L + 323.5, dy: 3, largura: 230, altura: 30 },
    avisoNaoEnviada: {
      x: L + 310.2,
      dy: 12,
      corpo: 12,
      espacamento: 1,
      largura: 256.73,
      alinhamento: 'center',
      negrito: true,
      cor: CORES.alerta,
    },
    caixaChave: { x: L + 310.2, dy: 35, largura: 256.73, altura: 23 },
    rotuloChave: {
      x: L + 314,
      dy: 37,
      corpo: TIPOGRAFIA.rotuloCampo,
      espacamento: 0.5,
    },
    valorChave: {
      x: L + 310.2,
      dy: 45.5,
      corpo: TIPOGRAFIA.chaveAcesso,
      espacamento: 0.5,
      largura: 256.73,
      alinhamento: 'center',
      negrito: true,
    },
    caixaConsulta: { x: L + 310.2, dy: 58, largura: 256.73, altura: 40 },
    consulta: [
      {
        x: L + 310.2,
        dy: 65,
        corpo: TIPOGRAFIA.consultaAutenticidade,
        espacamento: 0.5,
        largura: 256.73,
        alinhamento: 'center',
      },
      {
        x: L + 310.2,
        dy: 75,
        corpo: TIPOGRAFIA.consultaAutenticidade,
        espacamento: 0.5,
        largura: 256.73,
        alinhamento: 'center',
      },
      {
        x: L + 310.2,
        dy: 85,
        corpo: TIPOGRAFIA.consultaAutenticidade,
        espacamento: 0.5,
        largura: 256.73,
        alinhamento: 'center',
      },
    ],
  },

  natureza: {
    caixa: { x: L, dy: 98, largura: 310.2, altura: ALTURA_BANDA },
    rotulo: {
      x: L + 4,
      dy: 100,
      corpo: TIPOGRAFIA.rotuloCampo,
      espacamento: 0.5,
    },
    valor: {
      x: L + 4,
      dy: 108.5,
      espacamento: 0.5,
      largura: 302,
      ...VALOR_NEGRITO_CENTRALIZADO,
    },
  },

  protocolo: {
    caixa: { x: L + 310.2, dy: 98, largura: 256.73, altura: ALTURA_BANDA },
    rotulo: {
      x: L + 314.2,
      dy: 100,
      corpo: TIPOGRAFIA.rotuloCampo,
      espacamento: 0.5,
    },
    valor: {
      x: L + 314.2,
      dy: 108.5,
      espacamento: 0.5,
      largura: 248,
      ...VALOR_NEGRITO_CENTRALIZADO,
    },
  },

  inscricoes: [
    {
      caixa: { x: L, dy: 121, largura: 189.5, altura: ALTURA_BANDA },
      rotulo: {
        x: L + 4,
        dy: 123,
        corpo: TIPOGRAFIA.rotuloCampo,
        espacamento: 0.5,
      },
      valor: {
        x: L + 5,
        dy: 131.5,
        espacamento: 0.5,
        largura: 180,
        ...VALOR_NEGRITO_CENTRALIZADO,
      },
    },
    {
      caixa: { x: L + 189.5, dy: 121, largura: 187.93, altura: ALTURA_BANDA },
      rotulo: {
        x: L + 193.5,
        dy: 123,
        corpo: TIPOGRAFIA.rotuloCampo,
        espacamento: 0.5,
      },
      valor: {
        x: L + 194.5,
        dy: 131.5,
        espacamento: 0.5,
        largura: 178,
        ...VALOR_NEGRITO_CENTRALIZADO,
      },
    },
    {
      caixa: { x: L + 377.43, dy: 121, largura: 189.5, altura: ALTURA_BANDA },
      rotulo: {
        x: L + 381.43,
        dy: 123,
        corpo: TIPOGRAFIA.rotuloCampo,
        espacamento: 0.5,
      },
      valor: {
        x: L + 386.43,
        dy: 131.5,
        espacamento: 0.5,
        largura: 176,
        ...VALOR_NEGRITO_CENTRALIZADO,
      },
    },
  ],
};

/**
 * Cria a tripla caixa/rótulo/valor de um campo comum — o par que se repete
 * dezenas de vezes no desenho.
 *
 * @param {number} x - Origem horizontal da caixa.
 * @param {number} y - Topo da caixa.
 * @param {number} largura
 * @param {Object} [extras] - Sobrescreve as propriedades do valor.
 * @returns {{ caixa: Object, rotulo: Object, valor: Object }}
 */
function campo(x, y, largura, extras = {}) {
  return {
    caixa: { x, y, largura, altura: ALTURA_BANDA },
    rotulo: {
      x: x + 4,
      y: y + RECUO.rotulo,
      corpo: TIPOGRAFIA.rotuloCampo,
      espacamento: 0.5,
      largura: largura - 6,
    },
    valor: {
      x: x + 5,
      y: y + RECUO.valor,
      corpo: TIPOGRAFIA.conteudoCampo,
      espacamento: 0.5,
      largura: largura - 8,
      ...extras,
    },
  };
}

/**
 * Campo numérico: valor alinhado à direita, recuado do fio.
 *
 * @param {number} x
 * @param {number} y
 * @param {number} largura
 * @returns {Object}
 */
function campoNumerico(x, y, largura) {
  return {
    caixa: { x, y, largura, altura: ALTURA_BANDA },
    rotulo: {
      x: x + 4,
      y: y + RECUO.rotulo,
      corpo: TIPOGRAFIA.rotuloCampo,
      espacamento: 0.5,
      largura: largura - 6,
    },
    valor: {
      x,
      y: y + RECUO.valor,
      corpo: TIPOGRAFIA.conteudoCampo,
      espacamento: 0.5,
      largura: largura - 4,
      alinhamento: 'right',
    },
  };
}

/** Quadro do destinatário/remetente — apenas na primeira folha. */
export const DESTINATARIO = {
  titulo: {
    x: L,
    y: 226,
    corpo: TIPOGRAFIA.tituloBloco,
    espacamento: 0.5,
    negrito: true,
  },

  // Nome/Razão Social é o único campo do quadro que mantém o corpo padrão
  // (10 pt) e o alinhamento à esquerda; os demais usam o destaque de 8 pt
  // centralizado e negrito pedido pelo usuário — ver `VALOR_NEGRITO_CENTRALIZADO`.
  nome: campo(L, 232, 398, { negrito: true }),
  documento: campo(L + 398, 232, 94, VALOR_NEGRITO_CENTRALIZADO),
  dataEmissao: campo(L + 492, 232, 74.3, VALOR_NEGRITO_CENTRALIZADO),

  // Endereço e município ficam à esquerda (só negrito, sem centralizar) —
  // exceção pedida pelo usuário, diferente dos demais campos deste quadro.
  endereco: campo(L, 255, 320, { negrito: true }),
  bairro: {
    ...campo(L + 320, 255, 118.5, VALOR_NEGRITO_CENTRALIZADO),
    // Único campo com tipografia condicional no original: acima de 22
    // caracteres o corpo cai, em vez de quebrar linha. O piso continua
    // sendo o mínimo do MOC para conteúdo de campo.
    limiteCaracteres: 22,
    corpoReduzido: TIPOGRAFIA.conteudoCampo,
  },
  cep: campo(L + 438.5, 255, 53.5, VALOR_NEGRITO_CENTRALIZADO),
  dataSaida: campo(L + 492, 255, 74.3, VALOR_NEGRITO_CENTRALIZADO),

  municipio: campo(L, 278, 246.5, { negrito: true }),
  telefone: campo(L + 246.5, 278, 113, VALOR_NEGRITO_CENTRALIZADO),
  uf: campo(L + 359.5, 278, 40, VALOR_NEGRITO_CENTRALIZADO),
  inscricaoEstadual: campo(L + 399.5, 278, 92.5, VALOR_NEGRITO_CENTRALIZADO),
  horaSaida: campo(L + 492, 278, 74.3, VALOR_NEGRITO_CENTRALIZADO),
};

/**
 * Quadro de fatura/duplicatas. Diferente de todos os outros, é uma faixa de
 * largura elástica: as células dividem os 566,93 pt igualmente, até 7 por
 * banda, e o bloco cresce para baixo empurrando o resto do documento.
 *
 * O MOC 3.3.2 permite suprimi-lo quando não há duplicatas, desde que o
 * espaço vá para o quadro de produtos — que é o que o deslocamento faz.
 */
export const FATURA = {
  titulo: {
    x: L,
    y: 305,
    corpo: TIPOGRAFIA.tituloBloco,
    espacamento: 0.5,
    negrito: true,
  },
  topoBandas: 312,
  porBanda: 7,
  alturaBanda: ALTURA_BANDA,
  larguraTotal: LARGURA_UTIL,
  linhas: [
    { dy: 2, corpo: TIPOGRAFIA.rotuloCampo, espacamento: 0.3 },
    { dy: 9, corpo: TIPOGRAFIA.rotuloCampo, espacamento: 0.3 },
    { dy: 16, corpo: TIPOGRAFIA.rotuloCampo, espacamento: 0.3 },
  ],
  recuoCelula: 3,
};

/**
 * Quadro de cálculo do imposto. `y` já inclui o deslocamento da fatura.
 *
 * Alguns rótulos foram abreviados em relação ao original porque, a 6 pt,
 * o texto integral passaria a ocupar duas linhas e invadiria a linha do
 * valor (`VALOR APROX. DOS TRIBUTOS` → `VALOR APROX. TRIBUTOS`;
 * `BASE CÁLC. ICMS SUBST` → `B. CÁLC. ICMS ST`).
 *
 * Valor do PIS e Valor do COFINS foram acrescentados a pedido do usuário —
 * o MOC não prevê esses campos neste quadro (item 3.8.1), mas também não
 * proíbe adicioná-los. Cada um entra na linha existente correspondente,
 * logo antes do total daquela linha (PIS antes de Valor Total dos
 * Produtos, COFINS antes de Valor Total da Nota), o que levou as 6
 * colunas de cada linha a 7. As larguras foram redistribuídas a partir da
 * largura real de cada rótulo a 6 pt (medida via `item.width` do pdf.js,
 * não estimada) mais uma folga de 3 a 7 pt — bem menor que a folga
 * generosa do desenho original (que tinha até ~20 pt de sobra por campo),
 * mas confirmada suficiente para nenhum rótulo quebrar linha.
 *
 * @param {number} deslocamentoFatura
 * @returns {Object}
 */
export function calculoImposto(deslocamentoFatura) {
  const base = 195 + deslocamentoFatura;
  const linha1 = base + 120;
  const linha2 = base + 143;

  return {
    titulo: {
      x: L,
      y: base + 114,
      corpo: TIPOGRAFIA.tituloBloco,
      espacamento: 0.5,
      negrito: true,
    },

    baseCalculoIcms: campoNumerico(L, linha1, 100),
    valorIcms: campoNumerico(L + 100, linha1, 62),
    baseCalculoIcmsSt: campoNumerico(L + 162, linha1, 66),
    valorIcmsSt: campoNumerico(L + 228, linha1, 72),
    valorAproximadoTributos: campoNumerico(L + 300, linha1, 92),
    valorPis: campoNumerico(L + 392, linha1, 56),
    valorProdutos: {
      ...campoNumerico(L + 448, linha1, 118.93),
      fundo: FUNDO_DESTAQUE,
    },

    valorFrete: campoNumerico(L, linha2, 68),
    valorSeguro: campoNumerico(L + 68, linha2, 74),
    valorDesconto: campoNumerico(L + 142, linha2, 48),
    valorOutrasDespesas: campoNumerico(L + 190, linha2, 84),
    valorIpi: campoNumerico(L + 274, linha2, 80),
    valorCofins: campoNumerico(L + 354, linha2, 73),
    valorTotalNota: {
      ...campoNumerico(L + 427, linha2, 139.93),
      fundo: FUNDO_DESTAQUE,
    },
  };
}

/**
 * Quadro do transportador e dos volumes.
 *
 * @param {number} deslocamentoFatura
 * @returns {Object}
 */
export function transporte(deslocamentoFatura) {
  const base = 255 + deslocamentoFatura;
  const linha1 = base + 120;
  const linha2 = base + 143;

  return {
    titulo: {
      x: L,
      y: base + 114,
      corpo: TIPOGRAFIA.tituloBloco,
      espacamento: 0.5,
      negrito: true,
    },

    nome: campo(L, linha1, 248.5),
    modalidadeFrete: campo(L + 248.5, linha1, 90),
    codigoAntt: campo(L + 338.5, linha1, 50),
    placa: campo(L + 388.5, linha1, 62),
    ufVeiculo: campo(L + 450.5, linha1, 22),
    documento: campo(L + 472.5, linha1, 94.43),

    endereco: campo(L, linha2, 338.5),
    municipio: campo(L + 338.5, linha2, 112),
    uf: campo(L + 450.5, linha2, 22),
    inscricaoEstadual: campo(L + 472.5, linha2, 94.43),

    volumes: {
      topo: base + 166,
      passo: ALTURA_BANDA,
      colunas: [
        {
          chave: 'quantidade',
          rotulo: 'QUANTIDADE',
          x: L,
          largura: 68.5,
          alinhamento: 'right',
        },
        { chave: 'especie', rotulo: 'ESPÉCIE', x: L + 68.5, largura: 100.5 },
        { chave: 'marca', rotulo: 'MARCA', x: L + 169, largura: 100.5 },
        {
          chave: 'numeracao',
          rotulo: 'NUMERAÇÃO',
          x: L + 269.5,
          largura: 100.5,
        },
        // 102,5 e não os 102,6 do original: a coluna seguinte começa
        // em L+472,5, então 102,6 fazia a banda somar 567,03 pt e
        // vazar 0,1 pt para fora da folha.
        {
          chave: 'pesoBruto',
          rotulo: 'PESO BRUTO',
          x: L + 370,
          largura: 102.5,
          alinhamento: 'right',
        },
        {
          chave: 'pesoLiquido',
          rotulo: 'PESO LÍQUIDO',
          x: L + 472.5,
          largura: 94.43,
          alinhamento: 'right',
        },
      ],
    },
  };
}

/**
 * Colunas do quadro "Dados dos Produtos/Serviços", na ordem que a tabela
 * 3.8.1 do MOC determina.
 *
 * As doze colunas que o item 3.1.7 proíbe suprimir estão todas presentes:
 * código, descrição, NCM, CST, CFOP, unidade, quantidade, valor unitário,
 * valor total, base de cálculo do ICMS próprio, valor do ICMS próprio e
 * alíquota do ICMS. Desconto, IPI e as duas colunas de ST são
 * acréscimos permitidos pelo mesmo item.
 *
 * `x`/`largura` posicionam a moldura; `alinhamento` vale para o conteúdo.
 * As larguras somam exatamente `LARGURA_UTIL` junto com a coluna de
 * alíquotas — a verificação no fim do arquivo garante isso.
 */
const larguras = {
  codigo: 38,
  descricao: 110,
  ncm: 30,
  cst: 17,
  cfop: 20,
  unidade: 22,
  quantidade: 28,
  valorUnitario: 33,
  valorDesconto: 31,
  valorTotal: 34,
  baseCalculoIcms: 33,
  baseCalculoIcmsSt: 33,
  valorIcms: 33,
  valorIcmsSt: 33,
  valorIpi: 31.93,
};

/** Acumula as origens horizontais a partir das larguras, na ordem declarada. */
function posicionarColunas(definicoes) {
  let cursor = L;
  return definicoes.map((definicao) => {
    const coluna = {
      ...definicao,
      x: cursor,
      largura: larguras[definicao.chave],
    };
    cursor += coluna.largura;
    return coluna;
  });
}

export const COLUNAS_PRODUTOS = posicionarColunas([
  { chave: 'codigo', rotulo: 'CÓDIGO', alinhamento: 'left', linhasRotulo: 1 },
  {
    chave: 'descricao',
    rotulo: 'DESCRIÇÃO DOS PRODUTOS / SERVIÇOS',
    alinhamento: 'left',
    linhasRotulo: 2,
  },
  { chave: 'ncm', rotulo: 'NCM/SH', alinhamento: 'center', linhasRotulo: 1 },
  { chave: 'cst', rotulo: 'CST', alinhamento: 'center', linhasRotulo: 1 },
  { chave: 'cfop', rotulo: 'CFOP', alinhamento: 'center', linhasRotulo: 1 },
  { chave: 'unidade', rotulo: 'UNID.', alinhamento: 'center', linhasRotulo: 1 },
  {
    chave: 'quantidade',
    rotulo: 'QUANT.',
    alinhamento: 'right',
    linhasRotulo: 1,
  },
  {
    chave: 'valorUnitario',
    rotulo: 'VALOR UNITÁRIO',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
  {
    chave: 'valorDesconto',
    rotulo: 'DESCONTO',
    alinhamento: 'right',
    linhasRotulo: 1,
  },
  {
    chave: 'valorTotal',
    rotulo: 'VALOR TOTAL',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
  {
    chave: 'baseCalculoIcms',
    rotulo: 'B. CÁLC. ICMS',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
  {
    chave: 'baseCalculoIcmsSt',
    rotulo: 'B. CÁLC. ICMS ST',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
  {
    chave: 'valorIcms',
    rotulo: 'VALOR ICMS',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
  {
    chave: 'valorIcmsSt',
    rotulo: 'VALOR ICMS ST',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
  {
    chave: 'valorIpi',
    rotulo: 'VALOR IPI',
    alinhamento: 'right',
    linhasRotulo: 2,
  },
]);

const fimDasColunas =
  COLUNAS_PRODUTOS[COLUNAS_PRODUTOS.length - 1].x +
  COLUNAS_PRODUTOS[COLUNAS_PRODUTOS.length - 1].largura;

/**
 * Coluna de alíquotas: um rótulo agrupador sobre duas subcolunas. É a única
 * do quadro com cabeçalho em dois níveis.
 */
export const COLUNA_ALIQUOTAS = {
  grupo: { x: fimDasColunas, largura: 40, altura: 7.5, rotulo: 'ALÍQUOTAS' },
  subcolunas: [
    { chave: 'aliquotaIcms', rotulo: 'ICMS', x: fimDasColunas, largura: 20 },
    { chave: 'aliquotaIpi', rotulo: 'IPI', x: fimDasColunas + 20, largura: 20 },
  ],
  alturaSubcabecalho: 7.5,
};

export const PRODUTOS = {
  alturaCabecalho: 15,
  alturaPadraoLinha: 15,
  /** Folga vertical que o original somava à altura medida da descrição. */
  folgaVerticalLinha: 10,
  /** Recuo do conteúdo dentro da célula. */
  recuoValor: 4,
  /** Recuo lateral do texto dentro da célula. */
  recuoHorizontal: 1.5,

  /** Título e topo da tabela na primeira folha, antes do deslocamento da fatura. */
  primeiraFolha: {
    titulo: 452,
    tabela: 458,
  },
  /** Título e topo da tabela nas folhas de continuação. */
  continuacao: { titulo: 175, tabela: 185 },

  /**
   * Limite vertical de quebra: `page.height - 160`. Os 160 pt reservados
   * cobrem o quadro de dados adicionais (95 pt a partir de 731,95) mais a
   * folga até a margem inferior.
   */
  limiteQuebra: PAGINA.altura - 160,
};

/** Quadro de dados adicionais e as marcações de rodapé. */
export const RODAPE = {
  topo: 731.95,
  titulo: {
    x: L,
    y: 726.95,
    corpo: TIPOGRAFIA.tituloBloco,
    espacamento: 0.5,
    negrito: true,
  },

  caixaComplementares: { x: L, y: 731.95, largura: 408, altura: 95 },
  rotuloComplementares: {
    x: L + RECUO_TEXTO_HERDADO,
    y: 734.5,
    corpo: TIPOGRAFIA.rotuloCampo,
    espacamento: 0.5,
  },
  valorComplementares: {
    x: L + 7.33,
    y: 743,
    corpo: TIPOGRAFIA.informacoesComplementares,
    espacamento: 0.5,
    largura: 400,
    altura: 82,
  },

  caixaFisco: { x: L + 408, y: 731.95, largura: 158.93, altura: 95 },
  rotuloFisco: {
    x: L + 408 + RECUO_TEXTO_HERDADO,
    y: 734.5,
    corpo: TIPOGRAFIA.rotuloCampo,
    espacamento: 0.5,
  },
  valorFisco: {
    x: L + 418,
    y: 743,
    corpo: TIPOGRAFIA.informacoesComplementares,
    espacamento: 0.5,
    largura: 138,
    altura: 82,
  },

  /** Carimbo informativo com o instante de geração do PDF, sem valor fiscal. */
  carimboImpressao: {
    x: L,
    y: 829,
    corpo: TIPOGRAFIA.carimboImpressao,
    espacamento: 0.3,
  },

  /**
   * MOC 3.10.1: permitida desde que não prejudique a legibilidade. Fica na
   * faixa entre `PRODUTOS.limiteQuebra` (681,89) e `RODAPE.topo` (731,95) —
   * 50 pt sempre em branco em toda folha, pois nenhuma linha de item é
   * desenhada abaixo do limite de quebra (ver `paginacao.js`). Por ficar
   * fora do quadro de dados adicionais, a mesma posição serve tanto para
   * homologação quanto para produção sem protocolo, sem colidir com a
   * tarja de homologação (que fica dentro do quadro, em y=776,95).
   */
  marcaDagua: {
    x: L,
    largura: LARGURA_UTIL,
    alinhamento: 'center',
    cor: CORES.marcaDagua,
    y: 686,
    corpoDestaque: 22,
    corpoMotivo: 15,
  },
};

/**
 * Confere na importação que as bandas fecham na largura útil. Uma coluna
 * digitada com 0,5 pt a mais desalinha toda a tabela sem erro nenhum em
 * tempo de execução — e só aparece no papel.
 */
const somaProdutos = fimDasColunas - L + COLUNA_ALIQUOTAS.grupo.largura;

if (Math.abs(somaProdutos - LARGURA_UTIL) > 0.05) {
  throw new Error(
    `[DANFE/grade] As colunas de produtos somam ${somaProdutos.toFixed(
      2,
    )} pt, ${`mas a largura útil da folha é ${LARGURA_UTIL} pt.`}`,
  );
}

/**
 * Confere que o conteúdo de uma banda de 23 pt cabe entre os fios com a
 * tipografia escolhida. Elevar `TIPOGRAFIA.conteudoCampo` sem revisar
 * `RECUO` faria o valor vazar por baixo do quadro, silenciosamente.
 */
const alturaOcupada = RECUO.valor + TIPOGRAFIA.conteudoCampo * 1.15;

if (alturaOcupada > ALTURA_BANDA - 0.5) {
  throw new Error(
    `[DANFE/grade] Uma banda de ${ALTURA_BANDA} pt não comporta rótulo de ` +
      `${TIPOGRAFIA.rotuloCampo} pt e conteúdo de ${TIPOGRAFIA.conteudoCampo} pt ` +
      `nos recuos atuais (ocupa ${alturaOcupada.toFixed(2)} pt).`,
  );
}
